import React from "react";
import { Box, Button, HStack, Text, Textarea, VStack, Wrap, WrapItem } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ADVANTAGE_ITEM_DEFS, RATING_SCALE, RatingValue, SalesDataLinkageInputState } from "./types";
import { RatingIcon, ScaleLegend, SectionTitle, StepHeader } from "./Common";

interface Props {
  state: SalesDataLinkageInputState;
  isEditable: boolean;
  namespace: string;
  onChange: (patch: Partial<SalesDataLinkageInputState>) => void;
}

const AdvantageStep: React.FC<Props> = ({ state, isEditable, namespace, onChange }) => {
  const { t } = useTranslation();
  const tr = (key: string) => t(`features.InvestmentScreening.${namespace}.salesDataLinkage.${key}`);

  return (
    <VStack align="stretch" spacing="16px">
      <SectionTitle title={tr("section.advantageTitle")} note={tr("section.advantageNote")} />

      <Box
        bg="#fff"
        border="1px solid #e2e8f0"
        borderRadius="10px"
        boxShadow="0 1px 3px rgba(16,42,80,.06)"
        p="16px 18px"
        maxW="900px"
      >
        <StepHeader step={1} title={tr("section.advantageStep1Title")} note={tr("section.advantageStep1Note")} />
        <ScaleLegend namespace={namespace} mb="20px" />

        <VStack align="stretch" spacing="12px">
          {ADVANTAGE_ITEM_DEFS.map((item) => (
            <Box key={item.key} p="16px" border="1px solid #eef2f7" borderRadius="10px" bg="#fbfcfe">
              <Box mb="12px">
                <Text fontSize="14.5px" fontWeight={700} color="#16233f">
                  {tr(`advantage.${item.labelKey}`)}
                </Text>
                <Text fontSize="12px" color="#94a3b8" mt="3px">
                  {tr(`advantage.${item.descKey}`)}
                </Text>
              </Box>

              <VStack align="stretch" spacing="10px" mb="14px">
                {item.radios.map((group) => {
                  const radioKey = `${item.key}.${group.key}`;
                  return (
                    <HStack key={group.key} spacing="14px" flexWrap="wrap">
                      <Text fontSize="12.5px" fontWeight={600} color="#475569" minW="150px">
                        {tr(`advantage.${group.labelKey}`)}
                      </Text>
                      <Wrap spacing="8px">
                        {group.options.map((opt) => {
                          const selected = state.advRadios[radioKey] === opt.v;
                          return (
                            <WrapItem key={opt.v}>
                              <Button
                                size="xs"
                                isDisabled={!isEditable}
                                variant={selected ? "solid" : "outline"}
                                colorScheme="blue"
                                leftIcon={
                                  <Box
                                    as="span"
                                    display="inline-block"
                                    w="6px"
                                    h="6px"
                                    borderRadius="50%"
                                    bg={selected ? "#fff" : "#2f6bd4"}
                                  />
                                }
                                onClick={() =>
                                  onChange({ advRadios: { ...state.advRadios, [radioKey]: opt.v } })
                                }
                              >
                                {tr(`advantage.${opt.labelKey}`)}
                              </Button>
                            </WrapItem>
                          );
                        })}
                      </Wrap>
                    </HStack>
                  );
                })}
              </VStack>

              <HStack spacing="12px" flexWrap="wrap" pt="12px" borderTop="1px dashed #e2e8f0">
                <Text fontSize="12.5px" fontWeight={600} color="#475569" minW="150px">
                  {tr("label.axisEvaluation")}
                </Text>
                <HStack spacing="8px" flexWrap="wrap">
                  {RATING_SCALE.map((r) => {
                    const selected = state.evals[item.key] === r.val;
                    return (
                      <Button
                        key={r.val}
                        size="sm"
                        isDisabled={!isEditable}
                        title={tr(`ratingScale.${r.labelKey}`)}
                        variant="outline"
                        borderColor={selected ? r.color : "#e2e8f0"}
                        color={selected ? "#fff" : "#b0b8c4"}
                        bg={selected ? r.color : "transparent"}
                        onClick={() =>
                          onChange({ evals: { ...state.evals, [item.key]: r.val as RatingValue } })
                        }
                      >
                        <RatingIcon val={r.val} size={16} />
                      </Button>
                    );
                  })}
                </HStack>
              </HStack>

              <Box mt="14px">
                <Text fontSize="12.5px" fontWeight={600} color="#475569" mb="6px">
                  {tr("label.qualitativeNoteLabel")}
                </Text>
                <Textarea
                  value={state.advNotes[item.key] ?? ""}
                  onChange={(e) =>
                    onChange({ advNotes: { ...state.advNotes, [item.key]: e.target.value } })
                  }
                  isDisabled={!isEditable}
                  placeholder={tr("label.qualitativeNotePlaceholder")}
                  rows={4}
                  fontSize="13px"
                  bg="#fff"
                  borderColor="#e2e8f0"
                  borderRadius="8px"
                />
              </Box>
            </Box>
          ))}
        </VStack>
      </Box>
    </VStack>
  );
};

export default AdvantageStep;
